// ============================================
// PropertyBlock — defineStruct-based uniform data
// ============================================

import {
  type StructDef,
  type StructWriter,
  type GPUTypeInfo,
  type GPUValue,
} from '../core/GPUTypes';
import { type IRenderContext } from '../core/IRenderContext';
import { type BufferHandle, NULL_HANDLE } from '../types';

/**
 * PropertyBlock wraps a `defineStruct`-based uniform struct with a
 * `StructWriter` for CPU-side writes and a GPU buffer for upload.
 *
 * Usage:
 * ```ts
 * const MyUniforms = defineStruct('MyUniforms', {
 *   color:     GPUType.f32().vec4(),
 *   intensity: GPUType.f32().build(),
 *   offset:    GPUType.f32().vec2(),
 * });
 *
 * const props = new PropertyBlock(ctx, MyUniforms);
 * props.set('color', [1, 0, 0, 1]);
 * props.set('intensity', 2.5);
 * props.apply(ctx);
 * ```
 */
export class PropertyBlock<
  TFields extends Record<string, GPUTypeInfo> = Record<string, GPUTypeInfo>,
> {
  /** The underlying struct definition. */
  readonly structDef: StructDef<TFields>;

  /** The CPU-side writer. Direct access for advanced usage. */
  readonly writer: StructWriter<TFields>;

  /** Whether any field has been modified since last `apply()`. */
  private _dirty = true;

  /** Optional GPU uniform buffer (created on demand via `createBuffer()`). */
  private _buffer: BufferHandle = NULL_HANDLE;
  private _ctx: IRenderContext | null = null;

  constructor(structDef: StructDef<TFields>) {
    this.structDef = structDef;
    this.writer = structDef.createWriter();
  }

  // -----------------------------------------------------------------------
  // Field Access
  // -----------------------------------------------------------------------

  /** Set a single field value. */
  set<K extends keyof TFields & string>(name: K, value: GPUValue): this {
    this.writer.set(name, value);
    this._dirty = true;
    return this;
  }

  /** Set multiple field values at once. */
  setAll(values: { [K in keyof TFields]?: GPUValue }): this {
    this.writer.setAll(values);
    this._dirty = true;
    return this;
  }

  /** Clear all fields to zero. */
  clear(): this {
    this.writer.clear();
    this._dirty = true;
    return this;
  }

  /** Whether any field changed since the last `apply()`. */
  get dirty(): boolean {
    return this._dirty;
  }

  // -----------------------------------------------------------------------
  // Apply to render context (per-field uniform path)
  // -----------------------------------------------------------------------

  /**
   * Push all fields to the context via `setUniform()`.
   * This works with the existing per-field uniform system.
   */
  apply(ctx: IRenderContext): void {
    const f32 = this.writer.float32;

    for (const field of this.structDef.fields) {
      const idx = field.offset / 4;
      const comps = field.type.components;

      if (field.type.isMatrix) {
        // Matrix: read the full column-major data
        const matSize = comps; // e.g. 16 for mat4x4
        ctx.setUniform(field.name, f32.subarray(idx, idx + matSize));
      } else if (comps === 1) {
        // Scalar
        if (field.type.scalar === 'u32') {
          ctx.setUniformInt(field.name, this.writer.uint32[idx]);
        } else {
          ctx.setUniform(field.name, f32[idx]);
        }
      } else {
        // Vector
        ctx.setUniform(field.name, f32.subarray(idx, idx + comps));
      }
    }

    this._dirty = false;
  }

  // -----------------------------------------------------------------------
  // GPU Buffer path (for direct uniform buffer binding, future use)
  // -----------------------------------------------------------------------

  /**
   * Ensure a GPU uniform buffer exists. Creates one if needed.
   * Returns the buffer handle.
   */
  ensureBuffer(ctx: IRenderContext): BufferHandle {
    if (this._buffer === NULL_HANDLE) {
      this._ctx = ctx;
      this._buffer = ctx.createBuffer({
        size: this.structDef.size,
        usage: ['uniform', 'copy-dst'],
        label: `PropertyBlock-${this.structDef.name}`,
      });
    }
    return this._buffer;
  }

  /**
   * Upload the struct data to the GPU buffer.
   * Creates the buffer if it doesn't exist yet.
   */
  upload(ctx: IRenderContext): BufferHandle {
    const handle = this.ensureBuffer(ctx);
    if (this._dirty) {
      ctx.updateBuffer(handle, this.writer.float32, 0);
      this._dirty = false;
    }
    return handle;
  }

  /** Get the GPU buffer handle (NULL_HANDLE if not created). */
  get bufferHandle(): BufferHandle {
    return this._buffer;
  }

  /** Raw ArrayBuffer for direct access. */
  get buffer(): ArrayBuffer {
    return this.writer.buffer;
  }

  /** Float32Array view of the data. */
  get float32(): Float32Array {
    return this.writer.float32;
  }

  /** Struct size in bytes. */
  get size(): number {
    return this.structDef.size;
  }

  /** Destroy the GPU buffer (if created). */
  destroy(): void {
    if (this._buffer !== NULL_HANDLE && this._ctx) {
      this._ctx.destroyBuffer(this._buffer);
      this._buffer = NULL_HANDLE;
      this._ctx = null;
    }
  }
}
